import { formComponents as fc } from "@/components/form";
import { ui } from "@/components/ui";
import InjectionDiarySaveLabel from "@/features/injection/components/InjectionDiarySaveLabel";
import InviteDialog from "@/features/invite/components/InviteDialog";
import FavouriteUsersComboBox from "@/features/profile/components/FavouriteUsersComboBox";
import { type SubmitHandler, useForm, zodResolver } from "@/utils/form";
import { useState } from "react";
import * as z from "zod";
import { useInsertSharedAttributes } from "../api/insertSharedAttributes";
import { useUpdateSharedAttributes } from "../api/updateSharedAttributes";
import type { SharedAttributeJSON } from "../types";
import UpdateAllCheckbox from "./UpdateAllCheckbox";

const formSchema = z.object({
	contact_id: z.string().uuid(),
});

interface Props {
	sharedAttributes?: SharedAttributeJSON;
	initialContactId?: string | null;
	organisationId: string;
}

const SharedAttributeContactPerson = ({
	sharedAttributes,
	initialContactId,
	organisationId,
}: Props) => {
	const { mutate: updateContactPerson } = useUpdateSharedAttributes();
	const { mutate: insertContactPerson } = useInsertSharedAttributes();
	const [updateAll, setUpdateAll] = useState(true);

	const hasAssociatedItems = Boolean(
		sharedAttributes?.events?.length ||
			sharedAttributes?.diaries?.length ||
			sharedAttributes?.trackers?.length,
	);

	type FormSchemaType = z.infer<typeof formSchema>;

	const form = useForm<FormSchemaType>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			contact_id: initialContactId ?? undefined,
		},
	});

	const onSubmit: SubmitHandler<FormSchemaType> = (formData) => {
		if (sharedAttributes?.shared_attribute_id) {
			if (updateAll) {
				updateContactPerson({
					shared_attribute_id: sharedAttributes.shared_attribute_id,
					contact_id: formData.contact_id,
				});
			} else {
				insertContactPerson({
					contact_id: formData.contact_id,
				});
			}
		} else {
			insertContactPerson({
				contact_id: formData.contact_id,
			});
		}
	};

	return (
		<div className="space-y-4">
			<ui.Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="flex flex-col gap-y-8 text-lg"
				>
					<div className="">
						<InjectionDiarySaveLabel
							label="Contact Person"
							data={{ contact_id: form.getValues("contact_id") }}
						/>
						<div className="flex flex-col gap-y-2">
							<FavouriteUsersComboBox
								organisationId={organisationId}
								placeholder="Contact Person"
								name="contact_id"
							/>

							<InviteDialog />
						</div>
					</div>
					<ui.DialogClose asChild>
						<fc.SaveButton />
					</ui.DialogClose>
				</form>
			</ui.Form>
			{hasAssociatedItems && sharedAttributes && (
				<UpdateAllCheckbox checked={updateAll} onCheckedChange={setUpdateAll} />
			)}
		</div>
	);
};

export default SharedAttributeContactPerson;
